import { useNavigate } from '@tanstack/react-router';
import NoItems from '@/components/ui/NoItems.tsx';
import { BillRowVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { AddManualLineParams } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import { notifyActionUnavailable } from '@/features/dashboard/bookingChanges/utils/actionPlaceholder.ts';
import useBookingBillView from '@/features/dashboard/bookingChanges/hooks/useBookingBillView.ts';
import {
  BookingBillView,
  BookingBillViewSkeleton,
} from '@/features/dashboard/bookingChanges/components/bill';
import {
  EmbeddedClientBill,
  EmbeddedParticipantBill,
  EmbeddedBillProps,
} from '@/features/dashboard/bookingChanges/components/panel';

interface BookingBillPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  bookingId: string;
  isEditing: boolean;
  onlyChanged: boolean;
  onOnlyChangedChange: (onlyChanged: boolean) => void;
  /** Akcja wiersza — otwiera modal edycji pozycji trzymany przez ekran. */
  onRowAction: (row: BillRowVm) => void;
  /** Dopisanie wolnej linii — trafia prosto do widoków, bez adapterów. */
  onAddManualLine: (params: AddManualLineParams) => void;
  /** Osoba, z której ekranu przyszedł operator — jej kafel dostaje ciemniejszą ramkę. */
  currentPersonId?: string;
}

/**
 * Zakładka „Rezerwacja” — jeden i ten sam widok na ekranie uczestnika,
 * zapisującego i rezerwacji. Rozwinięty kafel osoby wpuszcza pełny ekran tej
 * osoby, więc żadna pozycja nie jest opisana w systemie dwa razy.
 */
const BookingBillPanel = ({
  bookingId,
  isEditing,
  onlyChanged,
  onOnlyChangedChange,
  onRowAction,
  onAddManualLine,
  currentPersonId,
  ...props
}: BookingBillPanelProps) => {
  const navigate = useNavigate();
  const { view, isLoading } = useBookingBillView(bookingId, isEditing);

  const openBooking = () =>
    navigate({
      to: '/app/bookings/$bookingId/booking',
      params: { bookingId },
    });

  if (isLoading) {
    return <BookingBillViewSkeleton {...props} />;
  }

  if (!view) {
    return (
      <NoItems
        title="Brak danych rezerwacji"
        description="Ta rezerwacja nie ma jeszcze rachunku."
        {...props}
      />
    );
  }

  const embeddedProps: EmbeddedBillProps = {
    bookingId,
    bookingCode: view.bookingCode,
    isEditing,
    onOpenBooking: openBooking,
    onRowAction,
  };

  return (
    <BookingBillView
      data={view}
      isEditing={isEditing}
      onlyChanged={onlyChanged}
      currentPersonId={currentPersonId}
      renderParticipant={(participantId) => (
        <EmbeddedParticipantBill
          {...embeddedProps}
          participantId={participantId}
          onlyChanged={onlyChanged}
          onOnlyChangedChange={onOnlyChangedChange}
          onAddManualRow={onAddManualLine}
        />
      )}
      renderClient={() => <EmbeddedClientBill {...embeddedProps} />}
      onRowAction={onRowAction}
      // Zniżka całej rezerwacji nie ma uczestnika (reguła D10), a przycisk stoi
      // poza sekcjami — sekcję dobiera rachunek.
      onAddDiscount={() => onAddManualLine({ discount: true })}
      // Dodanie uczestnika to osobna operacja domenowa, a nie pozycja ręczna —
      // paczka zmian nie ma dla niej typu pozycji.
      onAddParticipant={() =>
        notifyActionUnavailable('Dodanie uczestnika do rezerwacji')
      }
      onAddManualRow={onAddManualLine}
      {...props}
    />
  );
};

export default BookingBillPanel;
